import { assign, createMachine, Interpreter, spawn } from 'xstate-v4'
import { logger } from '../../../utility/logger'
import { FavouriteCampsiteDetails, Wishlist } from '../types'
import { getFavouriteWishlistMachine } from './favouriteWishlist.machine'

interface MachineWishlist extends Wishlist {
  hasCampsite?: boolean
  note?: string
}

export interface FavouriteCampsiteMachineContext {
  id?: string
  addFavouriteRepeats: number
  added?: Date
  favouriteDetails: FavouriteCampsiteDetails
  favouriteNote: string
  isFavourite: boolean
  wishlists: Record<string, MachineWishlist>
  filteredWishlists: Record<string, MachineWishlist>
  changes: Record<string | number, { initial: boolean; current: boolean }>
  errors: Record<string, string>
}

type FavouriteCampsiteMachineState =
  | { value: 'initial'; context: FavouriteCampsiteMachineContext }
  | { value: 'showingMainView'; context: FavouriteCampsiteMachineContext }
  | {
      value: { showingMainView: 'defaultFavourite' }
      context: FavouriteCampsiteMachineContext
    }
  | {
      value: { showingMainView: 'wishlists' }
      context: FavouriteCampsiteMachineContext
    }

type FavouriteCampsiteMachineEvents =
  | { type: 'ADD_TO_FAVOURITES' }
  | { type: 'REMOVE_FROM_FAVOURITES' }
  | { type: 'REMOVE_FROM_WISHLIST' }
  | { type: 'SAVE_TO_NEW_WISHLIST' }
  | { type: 'REFETCH_WISHLISTS' }
  | { type: 'SET_NOTE_FOR_FAVOURITE'; note: string }
  | { type: 'SHOW_ADD_TO_NEW_WISHLIST_FORM' }
  | { type: 'SHOW_ADD_TO_WISHLIST_FORM'; listId: string }
  | { type: 'SHOW_MAIN' }
  | { type: 'FILTER_WISHLISTS'; searchTerm: string }
  | { type: 'UPDATE_CHANGE'; listId: string | number; isSaved: boolean }
  | { type: 'RESET_CHANGES' }

export const createFavouriteMachine = ({
  campsiteId,
  favStore,
}: {
  campsiteId: string
  favStore: ReturnType<typeof useFavouritesStore>
}) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDMCGA3A9gVwE4EsAXMAYVQFsAHWIsAOnwDsj9UAbAYgk0fqawDW9NFjy0yVGsQbNCrNgn6YAxqjk8A2gAYAutp2JQlTFPw9DIAB6IATAFY7dAIxaAnAGYHNrQHYAHFo2PgBsADQgAJ6Ifq5adD52wYkALK5O-sFuAL5Z4SI4BMQS1LR0sAAWmADuTFAAsqhMAGr4YFV0EGBo2GyEAGIYBaWwYIRyjFAc+hbGpuZIVojBPj7xTn4+7k6e9nY2yeFRCE5Owcl0NsHuWsm3O9fuOXmDYkUUJdIV1bUNza3tnW6vQGokK9BGY1qUycBgWsxY81A1gQy1WPnWm22ey8B0iiCcl1Wdm2vlcNk8Dh8TxA+VepHeUnBlRqE1+jBabQ6XVQPX6LzBZWZtRBQ2IHAASgBROoAeSakoA+n1xTK6kqAIJNGUAVXFAEkACqSgDK0zhJgRjAsyJc5LoMS0ZzS+z86UO+IJNjoxOuSTsWjsyRO1NpYOKjMF31ZjXZ-y5QL5oOGQomADlMInRWAOOqACK5hUGmUarW6w0ms1GC3qK0LZHk1J0VzBPwBG6uZLEmzu44nRw+gNBJJ+YLNkP88QM5NR+oxjkA7m8kV0uih2jisDkTDoSAcSywQhqeioZDEXAACicAEoOGu3pJpyzZ39OYCecCJ9I72AN1udxBKxAeEa2tWxAi9TIyWCaCOwJR0exONx4iuG4g39TsqVyGlP3pB9PhTZ9Y1fRcPyTL8cPVCBOggPcDyPOgTzPS8b2-cNHx+Oc4zfJccNXCiqMgQDgLMWskSWFY1g2LYdhxBCTmCZCHh8ZI-GSHx7CcccyNwj4mRnNl53jd9MxXXBN23KFuF4GRBGEHC2Pw-TOOIhNlwFMy-1qRRGCwVQa30ITqxE0DjjsHxXHtZJ3GCckA2WPxorkpCQmuW4nHQsKtKzBy9KfAyuJIkz3PM9AoTAXBcEwXA6EoNg1GQKryD47ScsjPLnIXVzeI8iyJm83y1BEgLdBmILEUWULwsi6LYsSfxErxY53BHOgtBJVsHgcRIsrpVqvnal9OuMtzSlQKjLJ4PgfMwIRmuyqdHIOoijp47SGPOvqlD8obdECuZRImhsIubVstHbTsCR7VwAguFDyVddL3GSHawwe3KOMOozXqzd6IDKiqqpqurCAa3AmtYtG2ox56sdInGzrxz7ru+zRfpG81-pCmxVLiEGwtcYlVJHXEjlcZtnHClsCWSeHWxRyc8PR6NMagbA4EIAB1fAKjYbXCFgMpRnGSYrKu2y7t2yn9upwzVfVrWdb1g2IWN-qVEG1m9HZqtObrRAQi9VTopiZsbEuBajnStJvW2FxRz2JxO3l+9dKp5Wabtg8HfKXWD2do2LuspRbopxW08I221az7Wc6dw3ISZgb-N+mFRt9sSEAD6aWzFmLw7CRbTlUuhkmgoIBeUuxVNcZOdIja308r+2a9z-X6+Njhysq6ravqxqLdRsuF4ruNM81le65dryvo9xhhthH3LRCrug570P+4QwNzjWrYwbB05kiBFnntAi+VOQ1EdnnDgxoAASMoNYKjqOqPUqY-pPz9ggf0CkMRqQTukCGdgex7AimHJIZwVIdkAZpLCpdU7HzAe0CBtc87r0LmbG6dkWpW1AR1OgTDV75wblAN2LM75swfkBMaANkTzW9DFW4qRfTxx7MtPw8R-RrUdIGOwZJMLPC4UfHhmN+GXwLhMLgl0bIcIPgrOhRiaYmJYVfRu7tm56FbhzdBHdZGJH2LcDwjplGLSRk4C4GitDuB8DcLQGwQjAO4U5YxF8nFmMmFvQmu8Sb71ofPexhlHFr2ccIm+bi0EgQwT4+R-ilECx7GHOI4T1ijhJHsR4ND7IJKevk5JhSCLZ1XtAuBCC8wFiLAqVMkoEEaz1LAgAMjMg0SoZTijqGU4KGDY4KUuIGBIARggEjJEQ4kTY7C-2ilcQMlx4mGMSQ4npzsCKSioIQCIgz4EKhGYWYsEypkzJgfM40iy+jLNWd7SR7cJqbNhjsqejoDndmCe4CKCR-4qRiLCmw1y7G3O6ZA3pM5KIQANJgVMbR+l6z6I1aBmpFRjJ+QqaZcyFlrPGjaAW5xvDczCkizw7gw5ENuPECJBJEiqInli3JOK4wFOdsQSgMpeC0UPNIRi5VzyALBixDpNyunSvuWUOVCqwAsukf7BKci-GKMCbUxaywvRRK2PYOFNhpYSvYovPVeLnYYFqMS0lVRyUHgsUXa6JdtXYt1eA-VsAfUTD9WSnpIjb73zbl4yFrgQjIS-jFJ0gYB5HBCCQ7ZxCVi4Mxe0gxEabaeuYYU2NUB40Bp6ZvAmO9iak3JuGyVkbGHRvrY2wNhAk2lLBcJVlthOznE2EGZs6R0p9h7CsBSESziBhFb3HIWFGCYE6PABYOTaCpvKR3AAtPmxAJ6ZZuukEwFg7Aj3rI7jLHsrY1HQ0db4bwo59nXqVifNoD7x0ICDHEHBCQXX4OJIQxanZvR-yju4a46J1K-vLgw2mRVD2eOPRNTsaiwN4MnulF9w90jNmbKkbwNw-Cofobw7idMVxFMA6azByx4ipA7OFXwfhyTQcjkPJsNhmzLDcDLTwNGK33R1dWlyx1eLHxOsQFjIUeYWtSDEPsa0-ByTtD-MG6l0VpFo3kgqXU3rH3TJh5T2HH2A0Tg0x1MUp4qVOHJU4q0WlDinqOZGUnLYyY9XJ7GK50m4BUxgpD5wXBkgeHC8KCEwZehls2YkNx1KBkk-o6TVagsvUYwKb8v5tyQAix3dY0U6BIsHFcFYvofBJTUSlAMpzPAdgFiZqVwWCulG-IS0rtmgNYOcBsMtJwiP8cQNDc4qkrhJBCIEcKWXsKVu7bJ-L1n6A9VKhMMrkLrihO2UkNIS2dGTeOG4CKA4gjc3imcTrPaMNKePB9KAe36wuZHqcz9gDOyjihuiOgywM3SydSEYID31t0DPoOvdj8cPIjwyN5S4HxsEIQp2fsJIBb2EAZkZbB7HpQ5h9G1J73EDDYIxBibn8YjxCRepNwUS2zUOywF3L-72gk69eXJ5lAXnk97CpEeLreOj38K6ZsCEWzuBHmPWIC3fCbEh3l6HVdz48+PhuWAvI4fgrTTaROaiZbrH2PFSX57jj7Nl+SDNYMM3RWoyrznavl487C4Lk4wuTdi-N04KXg9Ayy5-icE4GxthXP84fDn6GZWC6R1TtHUGEKIdWn-RbU9ySeGd7H0nQjBeU9G6jyDxHglJCB2tRIYUwr+4DDn3hMry6Ds94ne1GmPAxe5uSI5yXTkR5WInPYfm2fR7W6rxvx8+cC8G6xr32Ccczq76pFRgCwnp8Q74vY9ekma4IoSgdPTKVk099BWXHhMQxOUm4QMArHDeGuKlh-fhMpR9sWPl3E-DW8EF0ke1CNuO4KZDnb+CODRTQxrRSzqTQTb53Ka79okoJp4qC71JTpIz377IoTuCLrHKnKroxQGabB6IrY5bv657u6trIFSxVY6KXanLqTpSW4hBojCx+6JAJSbpZBAA */
      id: 'favouriteCampsite',

      context: {
        addFavouriteRepeats: 0,
        campsiteId,
        favouriteDetails: {},
        favouriteNote: '',
        isFavourite: false,
        wishlists: {},
        changes: {},
        errors: {},
      },

      schema: {
        context: {} as FavouriteCampsiteMachineContext,
        events: {} as FavouriteCampsiteMachineEvents,
      },

      initial: 'initial',
      on: {
        UPDATE_CHANGE: {
          actions: ['updateChange'],
        },
        RESET_CHANGES: {
          actions: ['resetChanges'],
        },
      },
      states: {
        initial: {
          invoke: {
            src: 'getInitialData',
            onDone: {
              target: 'showingMainView',
              actions: assign({
                isFavourite: (_, e) => e.data.isFavourite,
                favouriteDetails: (_, e) => e.data.favouriteDetails,
                shouldAddToDefaultFavouriteOnInitial: (_, e) =>
                  e.data.shouldAddToDefaultFavouriteOnInitial,
              }),
            },
          },
        },
        showingMainView: {
          entry: ['initializeChanges'],
          type: 'parallel',
          on: {
            REFETCH_WISHLISTS: {
              target: ['.wishlists.setting', '.guestWishlists.setting'],
            },
          },
          states: {
            defaultFavourite: { ...defaultFavourite },
            guestWishlists: {
              initial: 'setting',
              states: {
                setting: {
                  invoke: {
                    src: 'setupGuestWishlists',
                    onDone: [
                      {
                        target: 'showingResults',
                        cond: 'hasAnyWishlists',
                        actions: ['spawnWishlistMachines'],
                      },
                      {
                        target: 'showingEmpty',
                      },
                    ],
                    onError: 'error',
                  },
                },
                showingEmpty: {},
                showingResults: {
                  tags: ['showingGuestWishlists'],
                },
                error: {},
              },
            },
            wishlists: {
              initial: 'setting',
              on: {
                SHOW_MAIN: {
                  target:
                    '#favouriteCampsite.showingMainView.wishlists.showingWishlist',
                  actions: assign({
                    favouriteNote: () => '',
                  }),
                },
              },
              states: {
                setting: {
                  invoke: {
                    src: 'getInitialWishlistsData',
                    onDone: [
                      {
                        target: 'showingWishlist',
                        cond: (_, { data }) => !!Object.keys(data).length,
                        actions: ['spawnWishlistMachines'],
                      },
                      { target: 'showingEmpty' },
                    ],
                    onError: 'error',
                  },
                },
                showingWishlist: {
                  tags: ['showingWishlists'],
                  on: {
                    SHOW_ADD_TO_NEW_WISHLIST_FORM: {
                      target: 'showingAddToNewWishlistForm',
                    },
                  },
                },
                showingEmpty: {
                  tags: ['showingEmptyWishlists'],
                  on: {
                    SHOW_ADD_TO_NEW_WISHLIST_FORM: {
                      target: 'showingAddToNewWishlistForm',
                    },
                  },
                },
                showingAddToNewWishlistForm: {
                  tags: ['showingAddToNewWishlistForm'],
                  on: {
                    SAVE_TO_NEW_WISHLIST: 'savingToNewWishlist',
                  },
                },
                savingToNewWishlist: {
                  invoke: {
                    src: 'saveToNewWishlist',
                    onDone: {
                      target: 'showingWishlist',
                      actions: [
                        'assignWishlists',
                        'assignFavouriteDetails',
                        (_, e) => {
                          e.data.callback()
                        },
                        assign({
                          changes: (context, e) => {
                            const listId = e.data.listId
                            const updatedChanges = { ...context.changes }
                            if (updatedChanges[listId]) {
                              updatedChanges[listId].current = true
                            } else {
                              updatedChanges[listId] = {
                                initial: false,
                                current: true,
                              }
                            }
                            return updatedChanges
                          },
                        }),
                      ],
                    },
                    onError: {
                      target: 'error',
                      actions: assign({
                        errors: (_, event) => {
                          if (event.data) {
                            return Object.fromEntries(
                              Object.entries(event.data).map(
                                ([key, messages]) => [
                                  key,
                                  (messages as string[])[0],
                                ],
                              ),
                            )
                          }
                          return {}
                        },
                      }),
                    },
                  },
                },
                error: {
                  on: {
                    SAVE_TO_NEW_WISHLIST: {
                      target: 'savingToNewWishlist',
                      actions: assign({
                        errors: (_) => ({}),
                      }),
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    {
      actions: {
        spawnWishlistMachines: assign({
          wishlists: (_, { data }) =>
            Object.fromEntries(
              Object.entries(data).map(([key, value]) => {
                const wishlistElementMachine =
                  getFavouriteWishlistMachine(favStore)
                const machine = spawn(
                  wishlistElementMachine.withContext({
                    ...value,
                    campsiteId,
                    favStore,
                  }),
                )
                return [key, machine]
              }),
            ),
        }),
        logError: () => {
          throw new Error('favouritesMachine logError not implemented')
        },
        assignFavouriteDetails: assign({
          favouriteDetails: (context, event) => {
            const updatedFavouriteDetails = {
              ...context.favouriteDetails,
            }
            const { listId, note } = event.data
            if (!updatedFavouriteDetails.wishlists) {
              updatedFavouriteDetails.wishlists = {}
            }
            updatedFavouriteDetails.wishlists[listId] = {
              id: listId,
              note,
              added: new Date().toISOString(),
            }
            return updatedFavouriteDetails
          },
        }),
        assignWishlists: assign({
          wishlists: (context, event) => {
            const { listId, listName, note, isOwner } = event.data
            const updatedWishlists = { ...context.wishlists }
            const wishlistElementMachine = getFavouriteWishlistMachine(favStore)
            updatedWishlists[listId] = spawn(
              wishlistElementMachine.withContext({
                id: listId,
                name: listName,
                status,
                hasCampsite: true,
                isOwner,
                note,
                itemCount: 1,
                campsiteId,
              }),
            )
            return updatedWishlists
          },
        }),
        initializeChanges: assign({
          changes: (context) => {
            const changes: Record<
              string | number,
              { initial: boolean; current: boolean }
            > = {
              'my-favourites': {
                initial: context.isFavourite,
                current: context.isFavourite,
              },
            }
            Object.entries(context.wishlists).forEach(([id, wishlist]) => {
              changes[id] = {
                initial: wishlist.hasCampsite || false,
                current: wishlist.hasCampsite || false,
              }
            })
            return changes
          },
        }),
        updateChange: assign({
          changes: (context, event) => {
            if (event.type !== 'UPDATE_CHANGE') {
              return context.changes
            }
            const { listId, isSaved } = event
            const updatedChanges = { ...context.changes }
            if (updatedChanges[listId]) {
              updatedChanges[listId].current = isSaved
            } else {
              updatedChanges[listId] = { initial: !isSaved, current: isSaved }
            }
            return updatedChanges
          },
        }),
        resetChanges: assign({
          changes: (context) => {
            const resetChanges = { ...context.changes }
            Object.keys(resetChanges).forEach((key) => {
              resetChanges[key].initial = resetChanges[key].current
            })
            return resetChanges
          },
        }),
      },
      guards: {
        isFavourite: (_, { data }: any) => data,
        hasAnyWishlists: (_, event) => {
          return !!Object.keys(event.data).length
        },
      },
      services: {
        // Default favourites:
        getInitialData: async ({ campsiteId }: any) => {
          await favStore.initFavouritesIfEmpty()
          const favouriteDetails = favStore.favourites[campsiteId]
          // this checks for is default favourite as they have added
          const isFavourite = !!favouriteDetails && favouriteDetails.added
          const wishlists = favStore.wishlists
          let shouldAddToDefaultFavouriteOnInitial = false
          if (Object.keys(wishlists).length <= 1 && !isFavourite) {
            shouldAddToDefaultFavouriteOnInitial = true
          }
          return Promise.resolve({
            isFavourite,
            favouriteDetails,
            shouldAddToDefaultFavouriteOnInitial,
          })
        },
        addDefaultFavourite: async ({ campsiteId }: any) => {
          await favStore.addFavourite(campsiteId)
        },
        removeDefaultFavourite: async ({ campsiteId }: any) => {
          return await favStore.removeFavourite(campsiteId)
        },
        // Wishlists:
        saveToNewWishlist: async (c: any, event: any) => {
          const response = await favStore.addFavouriteToNewWishlist(
            c.campsiteId,
            event.wishlistName,
            event.status,
          )
          return { ...response, callback: event.callback }
        },
        getInitialWishlistsData: async ({ favouriteDetails }: any) => {
          await favStore.initFavouritesIfEmpty()
          const lists: Record<string, MachineWishlist> = JSON.parse(
            JSON.stringify(favStore.wishlists),
          )
          const favouriteDetailsList = favouriteDetails?.wishlists || []
          for (const key in lists) {
            const details = favouriteDetailsList[key]
            lists[key].note = details ? details.notes : undefined
            lists[key].hasCampsite = !!details
          }
          return lists
        },
        // Guest wishlists:
        setupGuestWishlists: async ({ favouriteDetails }) => {
          logger('favourite.machine.setupGuestWishlists')
          await favStore.getAllGuestWishlists()
          const lists = favStore.wishlists
          const favouriteDetailsList = favouriteDetails?.wishlists || []
          for (const key in lists) {
            const details = favouriteDetailsList[key]
            lists[key].note = details ? details.notes : undefined
            lists[key].hasCampsite = !!details
          }
          return lists
        },
      },
    },
  )

export type FavouriteMachineInterpreter = Interpreter<
  FavouriteCampsiteMachineContext,
  any,
  Events
>

const defaultFavourite = {
  initial: 'initial',
  states: {
    initial: {
      always: [
        {
          target: 'adding',
          cond: ({ shouldAddToDefaultFavouriteOnInitial }: any) =>
            shouldAddToDefaultFavouriteOnInitial,
        },
        {
          target: 'setting',
        },
      ],
    },
    setting: {
      always: [
        {
          target: 'showingFavourite',
          cond: ({ isFavourite }: any) => isFavourite,
        },
        { target: 'showingNotFavourite' },
      ],
    },
    showingFavourite: {
      tags: ['showingDefaultFavourite'],
      on: {
        REMOVE_FROM_FAVOURITES: 'removing',
      },
    },
    showingNotFavourite: {
      tags: ['showingDefaultNotFavourite'],
      on: {
        ADD_TO_FAVOURITES: 'adding',
      },
    },
    error: {},
    favouriteRemoved: {
      after: {
        // using without timeout skips to next state
        1: 'showingNotFavourite',
      },
    },
    favouriteAdded: {
      after: {
        // using without timeout skips to next state
        1: 'showingFavourite',
      },
    },
    removing: {
      invoke: {
        src: 'removeDefaultFavourite',
        onDone: {
          target: 'favouriteRemoved',
          actions: assign({
            changes: (context) => {
              const updatedChanges = { ...context.changes }
              updatedChanges['my-favourites'].current = false
              return updatedChanges
            },
          }),
        },
        onError: 'error',
      },
    },
    adding: {
      invoke: {
        src: 'addDefaultFavourite',
        onDone: {
          target: 'favouriteAdded',
          actions: assign({
            changes: (context) => {
              const updatedChanges = { ...context.changes }
              updatedChanges['my-favourites'].current = true
              return updatedChanges
            },
          }),
        },
        onError: 'error',
      },
    },
  },
}

export type FavouriteMachineService = Interpreter<
  FavouriteCampsiteMachineContext,
  FavouriteCampsiteMachineState,
  FavouriteCampsiteMachineEvents
>
